import type { Lang } from "./i18n";
import type { MediaItem } from "./types";
import type { Status } from "./status";
import { DOMAINS, SITE } from "./domains.ts";

export type { MediaItem };
export type ProductStatus = Status;

export interface Product {
  id: number;
  slug: string;
  name: string;
  desc: Record<Lang, string>;
  longDesc?: Record<Lang, string>;
  tags: string[];
  status: ProductStatus;
  year: number;
  github?: string;
  gitlab?: string;
  live?: string;
  isPrivate?: boolean;
  media?: MediaItem[];
}

export const PRODUCTS: Product[] = [
  {
    id: 1,
    slug: "booru-dl",
    name: "booru.dl",
    desc: {
      en: "booru image scraper and browser with an Electron desktop app. browse and download from multiple booru sites with a clean dark UI.",
      jp: "ElectronデスクトップアプリのBooruイメージスクレイパー＆ブラウザ。クリーンなダークUIで複数のBooruサイトを閲覧・ダウンロード。",
    },
    longDesc: {
      en: "an Electron desktop app for browsing and downloading images from booru sites. supports Moebooru, Danbooru, Gelbooru, Shimmie2, and Szurubooru with auto-detection. features a paginated image/video grid, lightbox with keyboard navigation, video preview on hover, tag and blacklist autocomplete, multi-job download queue with pause/resume/stop, filename templates ({id}_{artist}_{characters} etc.), MD5 duplicate detection, and subfolder sorting by rating (safe/questionable/explicit).",
      jp: "BooruサイトのイメージブラウジングとダウンロードのためのElectronデスクトップアプリ。Moebooru、Danbooru、Gelbooru、Shimmie2、Szurubooruを自動検出でサポート。ページネーショングリッド、ライトボックス、タグ補完、マルチジョブダウンロードキュー、ファイル名テンプレート、MD5重複検出、レーティング別サブフォルダ整理を搭載。",
    },
    tags: ["electron", "python", "flask", "javascript"],
    status: "stable",
    year: 2026,
    gitlab: "https://gitlab.com/sp4r0ww/booru.dl",
  },
  {
    id: 2,
    slug: "sp4r0ww-com",
    name: DOMAINS.root,
    desc: {
      en: "personal website with live spotify, steam and git activity widgets. built with vanilla html/css/js on cloudflare pages.",
      jp: "Spotify、Steam、Gitアクティビティのライブウィジェットを備えた個人サイト。Cloudflare Pages上のバニラHTML/CSS/JSで構築。",
    },
    longDesc: {
      en: "personal website featuring: live Spotify now-playing widget with progress bar, Steam activity widget showing current/recent games, GitHub + GitLab contribution graphs for the last 28 days, a library page with books/projects/games/music filterable by category, notes and reviews system with track previews, liberty widget with glitch animation cycling 'freedom' in 70+ languages, and time-based backgrounds.",
      jp: "個人Webサイト。Spotifyリアルタイム再生ウィジェット、Steamアクティビティウィジェット、過去28日のGitHub + GitLabコントリビューショングラフ、カテゴリフィルタリング可能なライブラリページ、ノート＆レビューシステム、70以上の言語で「自由」を表示するグリッチアニメーション、時間帯別背景などを搭載。",
    },
    tags: ["javascript", "html", "css", "cloudflare"],
    status: "live",
    year: 2026,
    gitlab: "https://gitlab.com/sp4r0ww/sp4r0ww.com",
    live: SITE.main,
  },
  {
    id: 3,
    slug: "lab-sp4r0ww-com",
    name: "The Lab",
    desc: {
      en: "this site. project showcase and experiment space, built with react + vite + typescript.",
      jp: "このサイト。React + Vite + TypeScriptで構築したプロジェクトショーケースと実験スペース。",
    },
    longDesc: {
      en: "a dedicated showcase for things that are too specific, unfinished, experimental, or product-shaped to live comfortably on the main personal site. it keeps projects and products separate, supports bilingual English/Japanese copy, includes persistent theme and language settings, and gives each entry a detail page with status, stack, links, media, and longer notes.",
      jp: "メインの個人サイトだけでは置ききれない、特定用途のもの、未完成のもの、実験的なもの、プロダクトに近いものをまとめるための専用ショーケース。プロジェクトとプロダクトを分けて表示し、英語/日本語の切り替え、テーマと言語の保存、各項目の詳細ページ、ステータス、技術スタック、リンク、メディア、長めの説明を扱えるようにしている。",
    },
    tags: ["react", "typescript", "vite", "cloudflare"],
    status: "live",
    year: 2026,
    gitlab: "https://gitlab.com/sp4r0ww/lab.sp4r0ww.com",
    live: SITE.lab,
  },
  {
    id: 4,
    slug: "hideout-sp4r0ww-online",
    name: "Hideout",
    desc: {
      en: "Hideout is a personal microblog for thoughts, notes, updates, and other mental noise.",
      jp: "Hideoutは、考えごとやメモ、近況、頭の中のノイズをゆるく置いておく個人用マイクロブログです。",
    },
    longDesc: {
      en: "a lightweight personal microblog built for short posts, notes, updates, and loose fragments that do not need the weight of a full article. it focuses on quick publishing, readable archives, RSS output, media-friendly posts, and a quiet interface for keeping small thoughts online without turning them into a production.",
      jp: "短い投稿、メモ、近況、まとまりきらない断片を置くための軽量な個人マイクロブログ。長い記事にするほどではない内容を素早く公開できる場所として、読みやすいアーカイブ、RSS出力、メディア付き投稿、静かなUIを重視している。",
    },
    tags: ["vue", "typescript", "vite", "cloudflare"],
    status: "live",
    year: 2026,
    gitlab: "https://gitlab.com/sp4r0ww/hideout.sp4r0ww.com",
    live: SITE.hideout,
  },
];
